import { debounce } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetTransactionsCommonDropdownsQuery,
  useSearchTransactionAccountsMutation,
  useSearchTransactionClientsMutation,
  useGetTransactionClientsByIdsMutation,
  useGetTransactionAccountsByIdsMutation,
  useGetTransactionFirstDateQuery,
} from "src/entities/transactions/api/api";
import { ECalendarRange } from "src/shared/components/fields/DateRangeField/model/types";
import {
  EFilterCalendarType,
  EFilterType,
  IFilterModel,
} from "src/shared/components/Table/model/types";
import { useTableSearchParams } from "src/shared/hooks/useTableSearchParams";
import { INameValue } from "src/shared/types/common.types";
import { sanitizeQueryParams } from "src/shared/utils/url/query.helpers";
import {
  TransactionOperationType,
  TransactionsQuery,
  TransactionSort,
  TransactionStatus,
} from "../../../entities/transactions/model/types";

function mergeNameValueOptions<T extends string | number>(
  current: INameValue<T>[],
  next: INameValue<T>[],
) {
  const map = new Map<T, INameValue<T>>();

  current.forEach((item) => {
    map.set(item.value, item);
  });

  next.forEach((item) => {
    map.set(item.value, item);
  });

  return Array.from(map.values());
}

function normalizeMulti(value: unknown) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return undefined;

  return [value];
}

const OP_TYPES: TransactionOperationType[] = [
  "transfer",
  "increase",
  "decrease",
];
function isOperationType(v: unknown): v is TransactionOperationType {
  return typeof v === "string" && (OP_TYPES as readonly string[]).includes(v);
}
function parseOperationTypes(
  value: unknown,
): TransactionOperationType[] | undefined {
  const arr = normalizeMulti(value) as unknown[] | undefined;
  if (!arr?.length) return undefined;
  const parsed = arr.filter(isOperationType);

  return parsed.length ? parsed : undefined;
}

function parseSort(value: unknown): TransactionSort | undefined {
  if (!value) return undefined;
  if (typeof value !== "string") return undefined;

  try {
    const parsed = JSON.parse(value);
    if (!parsed?.field || !parsed?.direction) return undefined;

    return parsed as TransactionSort;
  } catch {
    return undefined;
  }
}

function sortToGridModel(sort?: TransactionSort): GridSortModel {
  if (!sort) return [];

  return [
    {
      field: String(sort.field),
      sort: sort.direction.toLowerCase() as "asc" | "desc",
    },
  ];
}

function getTodayRange() {
  const now = new Date();

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  return {
    dateFrom: start.toISOString(),
    dateTo: end.toISOString(),
  };
}

type ITransactionsSearchParams = {
  page?: string;
  per_page?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  dateCalendarRange?: ECalendarRange;
  operation_types?: string | string[];
  agencies?: string | string[];
  clients?: string | string[];
  accounts?: string | string[];
  statuses?: string | string[];
  sort?: string;
};

export function useTransactionsFilters() {
  const {
    paramsObject,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
  } = useTableSearchParams<ITransactionsSearchParams>({});
  const { data: commonDropdowns } = useGetTransactionsCommonDropdownsQuery();
  const [searchTransactionClients] = useSearchTransactionClientsMutation();
  const [searchTransactionAccounts] = useSearchTransactionAccountsMutation();
  const [getTransactionClientsByIds] = useGetTransactionClientsByIdsMutation();
  const [getTransactionAccountsByIds] =
    useGetTransactionAccountsByIdsMutation();
  const { data: transactionFirstDateData } = useGetTransactionFirstDateQuery();

  const {
    per_page,
    page,
    search,
    dateFrom,
    dateTo,
    dateCalendarRange,
    operation_types,
    agencies,
    clients,
    accounts,
    statuses,
    sort,
  } = paramsObject;

  const [clientsList, setClientsList] = useState<INameValue<number>[]>([]);
  const [accountsList, setAccountsList] = useState<INameValue<number>[]>([]);
  const [isClientsFetching, setIsClientsFetching] = useState(false);
  const [isAccountsFetching, setIsAccountsFetching] = useState(false);
  const selectedClientIds = useMemo(
    () => (normalizeMulti(clients)?.map(Number) as number[] | undefined) ?? [],
    [clients],
  );
  const selectedAccountIds = useMemo(
    () => (normalizeMulti(accounts)?.map(Number) as number[] | undefined) ?? [],
    [accounts],
  );
  const selectedAgencyIds = useMemo(
    () =>
      (normalizeMulti(agencies)?.map(Number).filter(Number.isFinite) as
        | number[]
        | undefined) ?? [],
    [agencies],
  );
  const selectedAgencyKey = useMemo(
    () => selectedAgencyIds.join(","),
    [selectedAgencyIds],
  );
  const defaultTodayRange = useMemo(() => getTodayRange(), []);
  const calendarMinDate = useMemo(() => {
    const fallbackDate = new Date("2026-04-22T00:00:00");

    if (!transactionFirstDateData?.date) {
      return fallbackDate;
    }

    const parsedDate = new Date(transactionFirstDateData.date);

    if (Number.isNaN(parsedDate.getTime())) {
      return fallbackDate;
    }

    return parsedDate;
  }, [transactionFirstDateData]);

  const calendarMaxDate = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    return today;
  }, []);
  const didInitDefaultDateRef = useRef(false);

  const loadClients = useCallback(
    async (value: string) => {
      const v = value.trim();
      if (v.length < 3) return;

      setIsClientsFetching(true);
      try {
        const resp = await searchTransactionClients({
          search: v,
          date_from: dateFrom,
          date_to: dateTo,
          agencies: selectedAgencyIds.length ? selectedAgencyIds : undefined,
          account_ids: normalizeMulti(accounts)?.map(Number) as
            | number[]
            | undefined,
        }).unwrap();

        setClientsList((prev) => mergeNameValueOptions(prev, resp.data));
      } catch {
        enqueueSnackbar("Не удалось загрузить клиентов", {
          variant: "error",
          handleClose: closeSnackbar,
        });
      } finally {
        setIsClientsFetching(false);
      }
    },
    [searchTransactionClients, dateFrom, dateTo, accounts, selectedAgencyIds],
  );

  const loadAccounts = useCallback(
    async (value: string) => {
      const v = value.trim();
      if (v.length < 3) return;

      setIsAccountsFetching(true);
      try {
        const resp = await searchTransactionAccounts({
          search: v,
          date_from: dateFrom,
          date_to: dateTo,
          agencies: selectedAgencyIds.length ? selectedAgencyIds : undefined,
          client_ids: normalizeMulti(clients)?.map(Number) as
            | number[]
            | undefined,
        }).unwrap();

        setAccountsList((prev) => mergeNameValueOptions(prev, resp.data));
      } catch {
        enqueueSnackbar("Не удалось загрузить аккаунты", {
          variant: "error",
          handleClose: closeSnackbar,
        });
      } finally {
        setIsAccountsFetching(false);
      }
    },
    [searchTransactionAccounts, dateFrom, dateTo, clients, selectedAgencyIds],
  );

  const debouncedLoadClients = useMemo(
    () => debounce(loadClients, 400),
    [loadClients],
  );
  const debouncedLoadAccounts = useMemo(
    () => debounce(loadAccounts, 400),
    [loadAccounts],
  );

  const sortParsed = useMemo(() => parseSort(sort), [sort]);
  const sortModelFromUrl = useMemo(
    () => sortToGridModel(sortParsed),
    [sortParsed],
  );

  const [gridSortModel, setGridSortModel] =
    useState<GridSortModel>(sortModelFromUrl);

  const sortPayloadFromGrid: TransactionSort | undefined = useMemo(() => {
    const first = gridSortModel?.[0];
    if (!first?.sort) return undefined;

    return {
      field: "date",
      direction: first.sort as "asc" | "desc",
    };
  }, [gridSortModel]);

  useEffect(() => {
    setGridSortModel(sortModelFromUrl);
  }, [sortModelFromUrl]);

  useEffect(() => {
    if (didInitDefaultDateRef.current) return;

    didInitDefaultDateRef.current = true;

    const hasAnyDateFilter = Boolean(dateFrom || dateTo || dateCalendarRange);

    if (hasAnyDateFilter) return;

    onFilter([
      { name: "dateFrom", value: defaultTodayRange.dateFrom },
      { name: "dateTo", value: defaultTodayRange.dateTo },
      { name: "dateCalendarRange", value: ECalendarRange.TODAY },
    ]);
  }, [dateFrom, dateTo, dateCalendarRange, onFilter, defaultTodayRange]);

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setGridSortModel(model);

      if (!model?.length || !model[0]?.sort) {
        onFilter([{ name: "sort", value: null }]);

        return;
      }

      const first = model[0];
      const payload: TransactionSort = {
        field: "date",
        direction: first.sort as "asc" | "desc",
      };

      onFilter([{ name: "sort", value: JSON.stringify(payload) }]);
    },
    [onFilter],
  );

  useEffect(() => {
    const hasClients = Array.isArray(clients)
      ? clients.length > 0
      : Boolean(clients);

    if (!hasClients) {
      setClientsList([]);
    }
  }, [clients]);

  useEffect(() => {
    const hasAccounts = Array.isArray(accounts)
      ? accounts.length > 0
      : Boolean(accounts);

    if (!hasAccounts) {
      setAccountsList([]);
    }
  }, [accounts]);

  const query: TransactionsQuery = useMemo(() => {
    const rawQuery: Record<string, unknown> = {
      search,
      per_page: per_page ? Number(per_page) : undefined,
      page: page ? Number(page) : 1,
      date_from: dateFrom,
      date_to: dateTo,
      operation_types: parseOperationTypes(operation_types),
      agencies: selectedAgencyIds.length ? selectedAgencyIds : undefined,
      client_ids: normalizeMulti(clients)?.map(Number),
      account_ids: normalizeMulti(accounts)?.map(Number),
      statuses: normalizeMulti(statuses) as TransactionStatus[] | undefined,
      sort: sortPayloadFromGrid,
    };

    return sanitizeQueryParams(rawQuery) as TransactionsQuery;
  }, [
    search,
    per_page,
    page,
    dateFrom,
    dateTo,
    operation_types,
    selectedAgencyIds,
    clients,
    accounts,
    statuses,
    sortPayloadFromGrid,
  ]);

  useEffect(() => {
    if (!selectedClientIds.length) return;

    const missingIds = selectedClientIds.filter(
      (id) => !clientsList.some((item) => Number(item.value) === id),
    );

    if (!missingIds.length) return;

    let cancelled = false;

    const run = async () => {
      try {
        const resp = await getTransactionClientsByIds({
          ids: missingIds,
        }).unwrap();

        if (cancelled) return;

        setClientsList((prev) => mergeNameValueOptions(prev, resp.data));
      } catch {
        // prefill best effort
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [selectedClientIds, clientsList, getTransactionClientsByIds]);

  useEffect(() => {
    if (!selectedAccountIds.length) return;

    const missingIds = selectedAccountIds.filter(
      (id) => !accountsList.some((item) => Number(item.value) === id),
    );

    if (!missingIds.length) return;

    let cancelled = false;

    const run = async () => {
      try {
        const resp = await getTransactionAccountsByIds({
          ids: missingIds,
        }).unwrap();

        if (cancelled) return;

        setAccountsList((prev) => mergeNameValueOptions(prev, resp.data));
      } catch {
        // prefill best effort
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [selectedAccountIds, accountsList, getTransactionAccountsByIds]);

  const prevAgencyKeyRef = useRef(selectedAgencyKey);

  useEffect(() => {
    if (prevAgencyKeyRef.current === selectedAgencyKey) return;

    prevAgencyKeyRef.current = selectedAgencyKey;

    debouncedLoadClients.clear();
    debouncedLoadAccounts.clear();

    setClientsList([]);
    setAccountsList([]);

    onFilter([
      { name: "clients", value: [] },
      { name: "accounts", value: [] },
    ]);
  }, [
    selectedAgencyKey,
    debouncedLoadClients,
    debouncedLoadAccounts,
    onFilter,
  ]);

  const filters: IFilterModel["filters"] = useMemo(
    () => [
      {
        name: "date",
        type: EFilterCalendarType.CALENDAR,
        title: "Период",
        minDate: calendarMinDate,
        maxDate: calendarMaxDate,
        value: {
          startDate: dateFrom ? new Date(dateFrom) : null,
          endDate: dateTo ? new Date(dateTo) : null,
          calendarRange: dateCalendarRange ?? null,
        },
      },
      {
        name: "agencies",
        type: EFilterType.MULTI_SELECT,
        title: "Компания",
        value: Array.isArray(agencies)
          ? agencies.map(Number)
          : agencies
            ? [Number(agencies)]
            : [],
        options: commonDropdowns?.data.agencies ?? [],
      },
      {
        name: "operation_types",
        type: EFilterType.MULTI_SELECT,
        title: "Тип операции",
        value: (normalizeMulti(operation_types) as string[] | undefined) ?? [],
        options: commonDropdowns?.data.transaction_types ?? [],
      },
      {
        name: "clients",
        loading: isClientsFetching,
        type: EFilterType.MULTI_AUTOCOMPLETE,
        title: "Клиент",
        value: Array.isArray(clients)
          ? clients.map(Number)
          : clients
            ? [Number(clients)]
            : [],
        options: clientsList,
        onInputChange: (_e, value, reason) => {
          if (reason !== "input") return;
          debouncedLoadClients(value);
        },
      },
      {
        name: "accounts",
        loading: isAccountsFetching,
        type: EFilterType.MULTI_AUTOCOMPLETE,
        title: "Аккаунт",
        value: Array.isArray(accounts)
          ? accounts.map(Number)
          : accounts
            ? [Number(accounts)]
            : [],
        options: accountsList,
        onInputChange: (_e, value, reason) => {
          if (reason !== "input") return;
          debouncedLoadAccounts(value);
        },
      },
      {
        name: "statuses",
        type: EFilterType.MULTI_SELECT,
        title: "Статус",
        value: (normalizeMulti(statuses) as string[] | undefined) ?? [],
        options: commonDropdowns?.data.transaction_short_statuses ?? [],
      },
    ],
    [
      dateFrom,
      dateTo,
      agencies,
      operation_types,
      clients,
      accounts,
      statuses,
      clientsList,
      accountsList,
      isClientsFetching,
      isAccountsFetching,
      debouncedLoadClients,
      debouncedLoadAccounts,
      commonDropdowns?.data.agencies,
      commonDropdowns?.data.transaction_types,
      commonDropdowns?.data.transaction_short_statuses,
      dateCalendarRange,
      calendarMaxDate,
      calendarMinDate,
    ],
  );

  return {
    query,
    filters,
    sortModel: gridSortModel,
    onSortModelChange,
    onPaginationModelChange,
    search,
    onSearch,
    onResetFilter,
    onFilter,
  };
}
